import { useState } from 'react';
import type { ReactNode } from 'react';
import { applyKeypad, centsFromInput } from '../../money/input';
import { formatMoney } from '../../mirror/time';
import type { T, UiLocale } from '../../messages';
import { KEYPAD_KEYS } from './model';

/**
 * **"How much was it?"** — the one place the number pad appears (B4).
 *
 * Her generic boutique lines — a scarf, a candle, a piece of jewellery — have
 * no price in the catalog; the price is typed at the till. So tapping one opens
 * this small sheet: a big amount, a calculator pad, and an optional note ("coral
 * scarf"). Nothing is added to the sale until the price is there, which is why
 * the open-price refusal (`money/waterfall.ts`) is now a state the cart can
 * never reach rather than a sentence the owner has to read.
 *
 * The pad and the keyboard both type into the same box: `onMouseDown` with
 * `preventDefault` keeps focus where it was, `applyKeypad` is the only editor
 * and `centsFromInput` the only thing that turns the text into money, so there
 * is no second number behind the screen.
 */
export function PriceSheet({
  name,
  initialPrice,
  initialNote,
  confirmLabel,
  currency,
  locale,
  t,
  onDone,
  onCancel,
}: {
  name: string;
  initialPrice: string;
  initialNote: string;
  confirmLabel: string;
  currency: string;
  locale: UiLocale;
  t: T;
  onDone: (priceText: string, note: string) => void;
  onCancel: () => void;
}): ReactNode {
  const [price, setPrice] = useState(initialPrice);
  const [note, setNote] = useState(initialNote);
  const cents = centsFromInput(price);
  const ready = cents !== null && cents > 0;

  return (
    <div className="pos-price" role="presentation" onClick={onCancel}>
      <div
        className="pos-price__panel"
        role="dialog"
        aria-label={t('checkout.priceSheetTitle', { name })}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="pos-price__title">{t('checkout.priceSheetTitle', { name })}</p>
        <p className="pos-price__amount" aria-hidden="true">
          {formatMoney(cents ?? 0, currency, locale)}
        </p>
        <input
          className="pos-price__input"
          aria-label={t('checkout.priceSheetAmount')}
          inputMode="decimal"
          placeholder="0.00"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && ready) onDone(price, note);
          }}
        />
        <div className="keypad" role="group" aria-label={t('checkout.keypadLabel')}>
          {KEYPAD_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className="keypad__key"
              aria-label={key === '⌫' ? t('checkout.keypadBackspace') : key}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setPrice((current) => applyKeypad(current, key, true))}
            >
              {key}
            </button>
          ))}
        </div>
        <label className="field pos-price__note">
          <span>{t('checkout.lineNoteHint')}</span>
          <input
            aria-label={t('checkout.lineNoteLabel', { name })}
            placeholder={t('checkout.lineNotePlaceholder')}
            maxLength={120}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <div className="pos-price__actions">
          <button type="button" className="button button--ghost" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="button button--primary"
            disabled={!ready}
            onClick={() => onDone(price, note)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
